import { Box, Collapse, Divider, Flex, useDisclosure } from '@televet/kibble-ui/build/chakra';
import { Alert } from '@televet/kibble-ui/build/components/Alert';
import { Button } from '@televet/kibble-ui/build/components/Button';
import { Icon } from '@televet/kibble-ui/build/components/Icon';
import { MenuItemProps } from '@televet/kibble-ui/build/components/Menu/components/MenuItem';
import { Select } from '@televet/kibble-ui/build/components/Select';
import { Spinner } from '@televet/kibble-ui/build/components/Spinner';
import { Text } from '@televet/kibble-ui/build/components/Text';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Control, Controller } from 'react-hook-form';
import { ClinicCarePlanFragment } from 'shared/types/graphql';
import { useUpgradeCalculator } from '../../../hooks/useUpgradeCalculator';
import { formatCurrency } from '../../../utils/currency';
import { useManageMembershipContext } from '../context/ManageMembershipContext';
import { IFormValues } from '../types';

interface UpgradeInformationProps {
  enrollmentId: string;
  plans?: ClinicCarePlanFragment[];
  upgradePlanOptions: MenuItemProps[];
  subscriptionPaymentLast4: string;
  control: Control<IFormValues>;
  setValue: (name: string, value: string | number) => void;
}

const UpgradeInformation = ({
  enrollmentId,
  plans,
  upgradePlanOptions,
  subscriptionPaymentLast4,
  control,
  setValue,
}: UpgradeInformationProps): JSX.Element => {
  const { isOpen: isFeeOpen, onToggle: onFeeToggle } = useDisclosure();
  const { isAbleToUpgrade, setIsAbleToUpgrade } = useManageMembershipContext();

  const firstAvailablePlanOption = useMemo(
    () => upgradePlanOptions.find((planOption) => !planOption.isDisabled),
    [upgradePlanOptions],
  );

  const [selectedPlanId, setSelectedPlanId] = useState(firstAvailablePlanOption?.value || '');
  const {
    currentPlan,
    selectedPlan,
    differenceBetweenPlans,
    paymentsMade,
    upgradeFee,
    isLoading,
    hasDisputedPayments,
  } = useUpgradeCalculator({
    enrollmentId,
    selectedPlanId,
    plans: plans || [],
  });

  const onUpgradePlanSelect = useCallback(
    (selectedItem: MenuItemProps): void => {
      const planId = selectedItem.value || '';
      setSelectedPlanId(planId);
      setValue('upgradePlanId', planId);
    },
    [setValue],
  );

  useEffect(() => {
    setIsAbleToUpgrade(selectedPlan.costPerBill >= currentPlan.costPerBill && !hasDisputedPayments);
  }, [currentPlan.costPerBill, hasDisputedPayments, selectedPlan.costPerBill, setIsAbleToUpgrade]);

  const highestTierPlan = useMemo(
    () =>
      plans?.reduce((prev, current) => {
        return prev && prev.planPricings[0]?.pricePerRenewal > current.planPricings[0]?.pricePerRenewal
          ? prev
          : current;
      }),
    [plans],
  );

  const alertMessage = useMemo(() => {
    if (hasDisputedPayments) {
      return 'Upgrade not available: Client has open or successful payment disputes';
    }
    if (currentPlan.id === highestTierPlan?.id) {
      return 'This client is currently on the highest tier plan and cannot be upgraded. Downgrades are not supported.';
    }
    return 'Downgrades are not supported.';
  }, [currentPlan.id, highestTierPlan?.id, hasDisputedPayments]);

  return (
    <Box pt={4}>
      <Box>
        <Text as="p" size="md" fontWeight="bold" pb={2}>
          Upgrade to which plan?
        </Text>
        <Controller
          name="upgradePlanId"
          control={control}
          as={Select}
          options={upgradePlanOptions}
          listProps={{
            onSelect: (selectedItem: MenuItemProps): void => onUpgradePlanSelect(selectedItem),
          }}
          defaultValue={selectedPlanId}
          isDisabled={currentPlan.id === highestTierPlan?.id}
        />
        {isLoading ? (
          <Flex alignItems="center" justifyContent="center">
            <Spinner />
          </Flex>
        ) : (
          <>
            {isAbleToUpgrade ? (
              <Box pt={4}>
                <Box>
                  <Flex justifyContent="space-between">
                    <Text fontWeight="bold">Upgrade Cost:</Text>
                    <Flex>
                      <Button
                        size="xs"
                        iconName={isFeeOpen ? 'chevronUp' : 'chevronDown'}
                        iconPosition="right"
                        variant="ghostNeutral"
                        onClick={onFeeToggle}
                        mr={1}
                      >
                        Show {isFeeOpen ? 'Less' : 'More'}
                      </Button>
                      <Text fontWeight="bold">{formatCurrency(upgradeFee, true)}</Text>
                    </Flex>
                  </Flex>
                  <Collapse in={isFeeOpen}>
                    <Box px={1} mt={1}>
                      <Flex justifyContent="space-between">
                        <Text variant="subtle" size="sm">
                          {selectedPlan.title || 'New Plan'} {selectedPlan.billingCycle} Cost
                        </Text>
                        <Text variant="subtle" size="sm">
                          {formatCurrency(selectedPlan.costPerBill, true)}
                        </Text>
                      </Flex>
                      <Flex justifyContent="space-between">
                        <Text variant="subtle" size="sm">
                          {currentPlan.title || 'Old Plan'} {currentPlan.billingCycle} Cost
                        </Text>
                        <Text variant="subtle" size="sm">
                          ({formatCurrency(currentPlan.costPerBill, true)})
                        </Text>
                      </Flex>
                      <Divider my={1} />
                      <Flex justifyContent="space-between">
                        <Text size="sm" fontWeight="bold">
                          Subtotal:
                        </Text>
                        <Text size="sm" fontWeight="bold">
                          {formatCurrency(differenceBetweenPlans, true)}
                        </Text>
                      </Flex>
                      <Flex justifyContent="space-between">
                        <Text variant="subtle" size="sm">
                          Payments Made on {currentPlan.title || 'Old Plan'}
                        </Text>
                        <Text variant="subtle" size="sm">
                          x{paymentsMade.quantity}
                        </Text>
                      </Flex>
                      <Divider my={1} />
                      <Flex justifyContent="space-between">
                        <Text size="sm" fontWeight="bold">
                          Total Owed:
                        </Text>
                        <Text size="sm" fontWeight="bold">
                          {formatCurrency(upgradeFee, true)}
                        </Text>
                      </Flex>
                    </Box>
                  </Collapse>
                </Box>
                <Flex mt={2}>
                  <Box>
                    <Icon name="infoCircle" size="sm" variant="subtle" />
                  </Box>
                  <Text variant="subtle" ml={1} size="sm">
                    Card on file {!!subscriptionPaymentLast4.length ? `ending in ` : ''}
                    <Text variant="subtle" fontWeight="bold" size="sm">
                      {subscriptionPaymentLast4}
                    </Text>{' '}
                    will be charged for the above amount. Any benefits used from previous plan will be subtracted from
                    the balance of the new plan.
                  </Text>
                </Flex>
              </Box>
            ) : (
              <>
                <Alert title={alertMessage} status={hasDisputedPayments ? 'error' : 'info'} hideCloseButton mt={4} />
              </>
            )}
          </>
        )}
      </Box>
    </Box>
  );
};

export default UpgradeInformation;
